<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { useFormModalWidget } from "../composables/useFormModalWidget.ts";
import FormFieldsWidget from "./FormFieldsWidget.vue";

const props = defineProps<{
  data: ReturnType<typeof useFormModalWidget>;
}>();

const { t } = useI18n();

const visibleFields = computed(() => {
  return props.data.fields.value
    .map((item) => {
      if (Array.isArray(item)) {
        return item.filter((field: any) => (typeof field.if === "function" ? field.if(props.data.model.value) : true));
      }
      const field = item as any;
      if (typeof field.if === "function" && !field.if(props.data.model.value)) {
        return null;
      }
      return item;
    })
    .filter((item) => {
      if (Array.isArray(item)) return item.length > 0;
      return item !== null;
    }) as any;
});
</script>

<template>
  <n-modal
    v-if="data.uiMode.value === 'modal'"
    :mask-closable="false"
    v-model:show="data.show.value"
    class="custom-card"
    preset="card"
    :style="{}"
    :title="data.title.value"
    :bordered="false"
    style="width: 580px"
    :segmented="{
      content: false,
      footer: false,
    }"
    :closable="!data.isLoading.value"
  >
    <n-spin :show="data.isLoading.value">
      <n-form require-mark-placement="left" :rules="data.rules.value" @submit.prevent="data.onSave" :ref="data.formEl" :model="data.model.value">
        <FormFieldsWidget v-model:value="data.model.value" :fields="visibleFields"></FormFieldsWidget>
      </n-form>
    </n-spin>

    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <n-button v-if="data.deletable.value && data.mode.value == 'edit'" type="error" @click="data.onDelete" quaternary>
          {{ t("common.actions.delete") }}
        </n-button>
        <div class="grow"></div>
        <n-button type="default" :disabled="data.isLoading.value" @click="data.show.value = false">
          {{ t("common.actions.cancel") }}
        </n-button>
        <n-button type="success" :disabled="data.isLoading.value" @click="data.onSave">
          {{ t("common.actions.save") }}
        </n-button>
      </div>
    </template>
  </n-modal>

  <n-drawer v-else v-model:show="data.show.value" :width="800" placement="right" :mask-closable="false" :closable="!data.isLoading.value">
    <n-drawer-content :title="data.title.value" :closable="!data.isLoading.value" :segmented="{ content: false, footer: false }">
      <n-spin :show="data.isLoading.value">
        <n-form require-mark-placement="left" :rules="data.rules.value" @submit.prevent="data.onSave" :ref="data.formEl" :model="data.model.value">
          <FormFieldsWidget v-model:value="data.model.value" :fields="visibleFields"></FormFieldsWidget>
        </n-form>
      </n-spin>

      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <n-button v-if="data.deletable.value && data.mode.value == 'edit'" type="error" @click="data.onDelete" tertiary>
            {{ t("common.actions.delete") }}
          </n-button>
          <div class="grow"></div>
          <n-button type="default" :disabled="data.isLoading.value" @click="data.show.value = false">
            {{ t("common.actions.cancel") }}
          </n-button>
          <n-button type="success" :disabled="data.isLoading.value" @click="data.onSave">
            {{ t("common.actions.save") }}
          </n-button>
        </div>
      </template>
    </n-drawer-content>
  </n-drawer>
</template>
